import { VF_MULTI_SELECT } from 'AppSrc/core/components/forms/form-base-components/v-f-multi-select';
import { fetchApproverGroupsForNetworkAsDisplayOptions } from 'AppSrc/core/approvers/approver-groups-actions';
import {
  INLINE_ERRORS,
  commonValidation,
  cleanUpFieldErrors,
  getDefaultFieldErrors,
} from 'AppSrc/core/components/forms/form-base-components/utility/common-validation';
import {
  FIXED,
} from 'AppSrc/core/components/forms/value-dependency-constants';
import { getArraySortedByProperty } from 'AppSrc/core/utility-functions/sort-functions';
import { APPROVER_GROUP_NAMES } from './approvers-section-constants';

const fieldId = APPROVER_GROUP_NAMES;

const validator = (params) => {
  const {
    values,
    field,
  } = params;
  const {
    optional,
  } = field;

  /* MAKE A DEFAULT ERRORS OBJECT */
  const errors = getDefaultFieldErrors();

  /* ERROR CHECKERS */
  const noValue = commonValidation.noValue(values[fieldId], VF_MULTI_SELECT);

  /* ADD ERRORS TO ERROR OBJECT */
  errors[INLINE_ERRORS].push((noValue && !optional) ? 'Please select approvers' : null);

  /* REMOVE BLANK ERRORS (because Formik gets confused) */
  return cleanUpFieldErrors(errors);
};


const initialValues = null;

/*
  For adding approver groups to a crq, we need to send the group id to add_approver_groups endpoint.
  However the getCrq endpoint will return approver groups by group name with no id filled in.
  As such, to be able to display already selected groups, we need the multi-select values to be group name
  rather than group id.
*/
const vFParams = {
  fieldTitle: 'Approver Groups',
  placeholder: 'Group names',
  displayType: 'big',
  withRedux: true,
  // boolean flags for custom approvers summary display
  hideSummaryTitle: true,
  showSummaryAsList: true,

  // make a new thunk by currying fetchApproverGroupsForNetworkAsDisplayOptions with the current params
  getDisplayOptionsOnMount: () => fetchApproverGroupsForNetworkAsDisplayOptions({
    fieldId,
    network: FIXED,
    preventRefresh: true,
  }),
  formatDisplayOptions: (params = {}) => {
    const {
      displayOptions,
      value,
    } = params;
    // If any approver values in state are readonly (I.E. have been saved to the server)
    // The associated displayOption should be readonly
    const formattedDisplayOptions = displayOptions.map((displayOption) => {
      const approverValue = value ? value.find((approver) => approver.name === displayOption.name) : null;

      const readOnly = approverValue ? approverValue.readOnly : false;
      console.log('readonly', readOnly);
      return ({
        text: displayOption.name,
        value: displayOption.name, // we need the value to be name rather than id in the multiselect
        // readOnly
      });
    });
    // Some existing values will not be in the display options as they represent inidividual user approvers
    // added within remedy. These should also appear in the list as read only.
    if (value) {
      value.forEach((approver) => {
        // is the value already in the array
        const approverInOptions = displayOptions.find((option) => option.name === approver.name);
        if (!approverInOptions) {
          // if not, add a readonly option to the formatted options
          const formattedOptionForApprover = ({
            text: approver.name,
            value: approver.name,
            readOnly: false,
          });
          formattedDisplayOptions.push(formattedOptionForApprover);
        }
      });
    }
    // display options for user groups should be sorted alphabetically by text
    // as there are so many and they come back from the server in a random order
    return getArraySortedByProperty(formattedDisplayOptions, 'text');
  },
  parseSelectedValue: (params = {}) => {
    const {
      displayOptions,
      valueToSet,
      currentFieldValue,
    } = params;
    const correctedValuesToSet = valueToSet.map((groupName) => {
      // if the approver already existed within the current field value
      // return the existing value. This will maintain the readOnly metadata
      const existingValue = currentFieldValue && currentFieldValue.find((group) => group.name === groupName);
      if (existingValue) {
        return existingValue;
      }

      // otherwise, if the selected groupName is not already selected in the list,
      // get the group out of the list of display options
      const approverGroup = displayOptions.find((displayOption) => displayOption.name === groupName);

      if (approverGroup) {
        return approverGroup;
      }

      // as a fallback, if the value is unrecognised
      // set the value as {id: groupName, name: groupName}
      console.warn('Approver group not found with name', groupName);
      return ({
        id: groupName,
        name: groupName,
      });
    });

    return correctedValuesToSet;
  },
  formatCurrentValue: (params = {}) => {
    const {
      value,
    } = params;

    // catches null
    if (!value) {
      return value;
    }

    // formik defines values as a collection of values from multiple fields and value as a single field's value. So here we use value to refer
    // to the fields value. However the field has multiple values, hense, singleValue
    const simplifiedValue = value.map((singleValue) => singleValue.name);

    return simplifiedValue;
  },
  getDisplayValue: (params = {}) => {
    const {
      value,
      noValue,
    } = params;

    if (!value) {
      return noValue;
    }

    // formik defines values as a collection of values from multiple fields and value as a single field's value. So here we use value to refer
    // to the fields value. However the field has multiple values, hense, singleValue
    const displayValue = value.map((singleValue) => singleValue.name);

    return displayValue;
  },
};

const approverGroupNames = {
  id: fieldId,
  validator,
  initialValues,
  vFParams,
  fieldType: VF_MULTI_SELECT,
};

export { approverGroupNames };
